import React from 'react';

interface Order {
  _id: string;
  user?: { name: string; phone: string };
  items: Array<{ name: string; quantity: string; icon: string }>;
  store: { name: string; location?: string };
  storeId?: { name: string };
  deliveryAddress: {
    addressLine: string;
    city?: string;
    pincode?: string;
  };
  status: string;
  totalAmount: number;
  orderDate: string;
  isDirectCustomOrder?: boolean;
  customOrderRequest?: {
    description: string;
    requestedItems: Array<{ name: string; quantity: string }>;
  };
  deliveryCharges?: number;
  isMultiStoreOrder?: boolean;
  storeNames?: string[];
  totalStores?: number;
}

interface OrdersListProps {
  orders: Order[];
  onAccept: (orderId: string) => void;
  onCancel?: (orderId: string) => void;
  onChat?: (orderId: string, userName: string, phone: string) => void;
  onCall?: (phone: string) => void;
  onBack: () => void;
}

const OrdersList: React.FC<OrdersListProps> = ({ orders, onAccept, onCancel, onChat, onCall, onBack }) => {
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button onClick={onBack} style={styles.backBtn}>
          ← Back
        </button>
        <h1 style={styles.title}>Available Orders</h1>
      </div>

      {orders.length === 0 ? (
        <div style={styles.emptyState}>
          <h2>📦 No orders available</h2>
          <p>Check back later for new orders</p>
        </div>
      ) : (
        <div style={styles.ordersList}>
          {orders.map((order) => (
            <div key={order._id} style={{
              ...styles.orderCard,
              ...(order.isDirectCustomOrder ? { borderLeft: '4px solid #7c3aed', background: '#faf5ff' } : {}),
              ...(order.isMultiStoreOrder ? { borderLeft: '4px solid #fbbf24', background: 'linear-gradient(135deg, #fef3c7, #fff)' } : {})
            }}>
              {/* Multi-Store Order Banner */}
              {order.isMultiStoreOrder && (
                <div style={{
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  color: 'white',
                  padding: '12px 15px',
                  borderRadius: '10px',
                  marginBottom: '15px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '20px', marginBottom: '5px' }}>🏆 Multi-Store Order!</div>
                  <div style={{ fontSize: '13px', opacity: 0.9 }}>
                    Pick up from {order.totalStores || order.storeNames?.length || 2} stores - Earn ₹70!
                  </div>
                  {order.storeNames && order.storeNames.length > 0 && (
                    <div style={{ fontSize: '12px', marginTop: '8px', fontWeight: 'bold' }}>
                      📍 {order.storeNames.join(' → ')}
                    </div>
                  )}
                </div>
              )}

              <div style={styles.orderHeader}>
                <div>
                  <h3 style={styles.orderTitle}>Order #{order._id.slice(-6)}</h3>
                  {order.isDirectCustomOrder && (
                    <span style={{
                      background: 'linear-gradient(135deg, #7c3aed, #ec4899)',
                      color: 'white',
                      padding: '3px 10px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      marginTop: '5px',
                      display: 'inline-block'
                    }}>
                      🛒 Direct Custom Order
                    </span>
                  )}
                  {order.isMultiStoreOrder && !order.isDirectCustomOrder && (
                    <span style={{
                      background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                      color: '#78350f',
                      padding: '3px 10px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      marginTop: '5px',
                      display: 'inline-block'
                    }}>
                      🏆 +₹20 Bonus
                    </span>
                  )}
                </div>
                <span style={styles.badge}>{order.status}</span>
              </div>

              {/* Direct Custom Order Info */}
              {order.isDirectCustomOrder && order.customOrderRequest && (
                <div style={{
                  background: '#f3e8ff',
                  padding: '12px',
                  borderRadius: '10px',
                  marginBottom: '15px',
                  border: '1px solid #c4b5fd'
                }}>
                  <h4 style={{ margin: '0 0 8px', color: '#7c3aed', fontSize: '14px' }}>📝 Customer Request</h4>
                  {order.customOrderRequest.description && (
                    <p style={{ margin: '0 0 8px', color: '#5b21b6', fontSize: '13px' }}>
                      {order.customOrderRequest.description}
                    </p>
                  )}
                  {order.customOrderRequest.requestedItems && order.customOrderRequest.requestedItems.length > 0 && (
                    <div>
                      <strong style={{ fontSize: '12px', color: '#6b21a8' }}>Items Needed:</strong>
                      <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                        {order.customOrderRequest.requestedItems.map((item, idx) => (
                          <li key={idx} style={{ fontSize: '13px', color: '#7c3aed' }}>
                            {item.name} - {item.quantity}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div style={{
                    background: '#fef3c7',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    marginTop: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span>💰</span>
                    <span style={{ fontSize: '12px', color: '#92400e' }}>
                      <strong>Delivery: ₹{order.deliveryCharges || 60}</strong> (Prepaid - Customer pays you)
                    </span>
                  </div>
                </div>
              )}

              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>📍 Delivery Address</h4>
                <p style={styles.address}>
                  {order.deliveryAddress.addressLine}
                  {order.deliveryAddress.city && `, ${order.deliveryAddress.city}`}
                  {order.deliveryAddress.pincode && ` - ${order.deliveryAddress.pincode}`}
                </p>
              </div>

              {!order.isDirectCustomOrder && (
                <div style={styles.section}>
                  <h4 style={styles.sectionTitle}>🏪 Store</h4>
                  <p>{order.store?.name || 'Direct Order'}</p>
                </div>
              )}

              {!order.isDirectCustomOrder && (
                <div style={styles.section}>
                  <h4 style={styles.sectionTitle}>🛒 Items ({order.items?.length || 0})</h4>
                  <div style={styles.itemsList}>
                    {order.items?.slice(0, 3).map((item, idx) => (
                      <div key={idx} style={styles.item}>
                        <span style={styles.itemIcon}>{item.icon}</span>
                        <span>{item.name} - {item.quantity}</span>
                      </div>
                    ))}
                    {order.items?.length > 3 && (
                      <p style={styles.moreItems}>+{order.items.length - 3} more items</p>
                    )}
                  </div>
                </div>
              )}

              <div style={styles.customerContact}>
                <h4 style={styles.sectionTitle}>👤 Customer</h4>
                <p style={styles.customerName}>{order.user?.name || 'Customer'}</p>
                <div style={styles.contactButtons}>
                  {order.user?.phone && (
                    <>
                      <button
                        onClick={() => onCall && onCall(order.user!.phone)}
                        style={styles.callBtn}
                        title="Call Customer"
                      >
                        📞 Call
                      </button>
                      <button
                        onClick={() => onChat && onChat(order._id, order.user!.name, order.user!.phone)}
                        style={styles.chatBtn}
                        title="Chat with Customer"
                      >
                        💬 Chat
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div style={styles.footer}>
                <div>
                  <div style={styles.amount}>₹{order.totalAmount}</div>
                  {(order as any).tip > 0 && (
                    <div style={{ color: '#28a745', fontSize: '14px', fontWeight: 'bold' }}>
                      💰 Tip: ₹{(order as any).tip}
                    </div>
                  )}
                </div>
                <div style={styles.actionButtons}>
                  <button
                    onClick={() => onAccept(order._id)}
                    style={styles.acceptBtn}
                  >
                    ✓ Accept
                  </button>
                  {onCancel && (
                    <button
                      onClick={() => onCancel(order._id)}
                      style={styles.cancelBtn}
                    >
                      ✕ Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles: any = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5',
    padding: '20px',
  },
  header: {
    marginBottom: '30px',
  },
  backBtn: {
    padding: '10px 20px',
    background: '#666',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    marginBottom: '15px',
  },
  title: {
    fontSize: '28px',
    color: '#333',
    margin: 0,
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    background: 'white',
    borderRadius: '15px',
  },
  ordersList: {
    display: 'grid',
    gap: '20px',
  },
  orderCard: {
    background: 'white',
    borderRadius: '15px',
    padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  },
  orderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    borderBottom: '2px solid #f0f0f0',
    paddingBottom: '15px',
  },
  orderTitle: {
    fontSize: '20px',
    color: '#333',
    margin: 0,
  },
  badge: {
    background: '#ffa500',
    color: 'white',
    padding: '5px 15px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  section: {
    marginBottom: '15px',
  },
  sectionTitle: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '8px',
  },
  address: {
    color: '#333',
    lineHeight: 1.5,
  },
  itemsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px',
    background: '#f9f9f9',
    borderRadius: '8px',
  },
  itemIcon: {
    fontSize: '24px',
  },
  moreItems: {
    color: '#fc8019',
    fontWeight: 'bold',
    marginTop: '5px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '20px',
    paddingTop: '15px',
    borderTop: '2px solid #f0f0f0',
  },
  amount: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#60b246',
  },
  acceptBtn: {
    padding: '12px 30px',
    background: '#fc8019',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  customerContact: {
    marginBottom: '15px',
    padding: '15px',
    background: '#f0f8ff',
    borderRadius: '10px',
    border: '1px solid #e0e0e0',
  },
  customerName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '10px',
  },
  contactButtons: {
    display: 'flex',
    gap: '10px',
  },
  callBtn: {
    flex: 1,
    padding: '10px 20px',
    background: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  chatBtn: {
    flex: 1,
    padding: '10px 20px',
    background: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  actionButtons: {
    display: 'flex',
    gap: '10px',
  },
  cancelBtn: {
    padding: '12px 30px',
    background: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
  },
};

export default OrdersList;
